// 익명 히어로 코드 — 엣지 연동(웹 동일 출처). FR-HERO-09~12.
// 모두 best-effort: 서버 미가동(M1 로컬)이면 null 반환 → ?h= URL 폴백(FR-HERO-13).
import { sanitizeHero, resumeUrl, familyBookUrl, type SavedHero } from "@greg-chick/shared";
import { EDGE_BASE } from "./apiBase";

export interface HeroCodeResult {
  code: string;
  url: string;
}

/** 히어로 저장/수정 → 코드 발급(또는 동일 코드 갱신). 실패 시 null. */
export async function postHero(hero: SavedHero): Promise<HeroCodeResult | null> {
  try {
    const res = await fetch(`${EDGE_BASE}/api/hero`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        code: hero.code,
        base: hero.base,
        parts: hero.parts,
        palette: hero.palette,
        nickname: hero.nickname,
      }),
    });
    if (!res.ok) return null;
    return (await res.json()) as HeroCodeResult;
  } catch {
    return null;
  }
}

/** 코드로 히어로 복원 조회. 화이트리스트 검증(FR-HERO-15) 후 반환. 실패 시 null. */
/**
 * 왜 못 찾았나.
 *
 * 🔴 **「그런 코드가 없다」와 「지금 못 물어봤다」는 다르다.** 아이는 종이 카드를 들고 서 있다.
 * 없다고 하면 부모는 카드가 잘못된 줄 알고 버리거나 캐릭터를 새로 만든다 — 그러면
 * 반에 올려 둔 그림책과의 연결이 끊긴다. 학교 와이파이·지하철에서 흔한 일이다.
 */
export type HeroFetch = { hero: SavedHero; why: "ok" } | { hero: null; why: "not_found" | "offline" };

export async function getHeroEx(code: string): Promise<HeroFetch> {
  try {
    const res = await fetch(`${EDGE_BASE}/api/hero/${encodeURIComponent(code)}`);
    // 404 만 「없다」다. 500·503 은 서버가 아픈 것이지 코드가 틀린 게 아니다
    if (res.status === 404) return { hero: null, why: "not_found" };
    if (!res.ok) return { hero: null, why: "offline" };
    const data = (await res.json()) as Record<string, unknown>;
    return {
      hero: sanitizeHero({ ...data, code, updatedAt: new Date().toISOString() }),
      why: "ok",
    };
  } catch {
    return { hero: null, why: "offline" }; // 와이파이가 끊긴 것 — 코드 탓이 아니다
  }
}

export async function getHero(code: string): Promise<SavedHero | null> {
  return (await getHeroEx(code)).hero;
}

/**
 * 공개 도메인. VITE_WEB_BASE 없으면 현재 출처.
 *
 * 🔴 이 계산을 화면마다 다시 쓰지 않는다. QR 주소를 만드는 곳이 둘로 갈리면
 * 한쪽만 고쳐지고, 종이에 찍힌 QR 이 화면에 뜬 주소와 다른 곳을 가리킨다.
 */
export function webBase(): string {
  return (import.meta.env.VITE_WEB_BASE?.trim() || window.location.origin).replace(/\/$/, "");
}

/** 복원 링크(QR·공유용). 반 코드를 주면 집에서 그림책까지 이어진다. */
export function heroRestoreUrl(code: string, classCode?: string | null): string {
  return resumeUrl(webBase(), code, classCode);
}

/**
 * 👪 **부모에게 보내는 주소** — 그림책 한 권을 읽기만 하는 곳.
 *
 * 🔴 `heroRestoreUrl`(`/h/…`)과 **다른 주소**다. 그건 아이가 이어서 만드는 길이라
 * 캐릭터를 그 기기에 되살린다 — 부모 폰에 보내면 안 되는 것이다.
 */
export function familyShareUrl(classCode: string, heroCode: string): string {
  return familyBookUrl(webBase(), classCode, heroCode);
}
